import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogContent,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Stack,
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/dateFormat';
import { getApiUrl } from '../../utils/apiUrl';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ForwardIcon from '@mui/icons-material/Forward';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import FlagIcon from '@mui/icons-material/Flag';
import { getUrgencyDisplay } from '../../utils/ticketUrgency';

const TicketDetailPage = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const navigate = useNavigate();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [copySnackbarText, setCopySnackbarText] = useState('');

  const base = getApiUrl('/api/tickets/').replace(/\/$/, '');

  const STATUS_DISPLAY = {
    pending: { label: 'รอรับเรื่อง', bgcolor: 'rgba(237, 108, 2, 0.1)', color: '#ed6c02', icon: <ScheduleIcon sx={{ fontSize: 18, color: 'inherit' }} /> },
    forward: { label: 'ส่งต่อ', bgcolor: 'rgba(2, 119, 189, 0.1)', color: '#0277bd', icon: <ForwardIcon sx={{ fontSize: 18, color: 'inherit' }} /> },
    in_progress: { label: 'กำลังดำเนินการ (รับเรื่องแล้ว)', bgcolor: 'rgba(132, 94, 194, 0.1)', color: '#845EC2', icon: <AutorenewIcon sx={{ fontSize: 18, color: 'inherit' }} /> },
    completed: { label: 'ดำเนินการเสร็จสิ้น', bgcolor: 'rgba(46, 125, 50, 0.1)', color: '#2e7d32', icon: <CheckCircleIcon sx={{ fontSize: 18, color: 'inherit' }} /> },
    cancelled: { label: 'ยกเลิก', bgcolor: 'rgba(0, 0, 0, 0.06)', color: 'rgba(0, 0, 0, 0.6)', icon: <CancelIcon sx={{ fontSize: 18, color: 'inherit' }} /> },
  };
  const getStatusDisplay = (status) => STATUS_DISPLAY[status] ?? { label: status ?? '-', bgcolor: 'rgba(0, 0, 0, 0.04)', color: 'rgba(0, 0, 0, 0.6)', icon: null };

  useEffect(() => {
    if (!id || !base) {
      setLoading(false);
      if (!id) setError('ไม่พบพารามิเตอร์ id ใน URL');
      else if (!base) setError('ไม่พบ API สำหรับ tickets');
      return;
    }
    setLoading(true);
    setError(null);
    axios
      .get(`${base}/${id}/`)
      .then(({ data }) => {
        setTicket(data);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || err?.message || 'โหลดไม่สำเร็จ');
        setTicket(null);
      })
      .finally(() => setLoading(false));
  }, [id, base]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 240 }}>
        <CircularProgress sx={{ color: '#845EC2' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
            รายละเอียด Ticket
          </Typography>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ borderRadius: 1 }}>
            กลับ
          </Button>
        </Box>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
            รายละเอียด Ticket
          </Typography>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ borderRadius: 1 }}>
            กลับ
          </Button>
        </Box>
        <Typography variant="body1" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
          ไม่พบรายการ
        </Typography>
      </Box>
    );
  }

  const attachments = Array.isArray(ticket.attachments) ? ticket.attachments : [];
  const logs = Array.isArray(ticket.logs) ? ticket.logs : [];
  const sortedStatusLogs = [...logs].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
  const isImage = (fileUrl) =>
    /\.(png|jpe?g|gif|webp)(\?|$)/i.test(String(fileUrl || '').split('/').pop() || '');

  const copyToClipboard = async (text) => {
    const value = String(text ?? '').trim();
    if (!value) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const el = document.createElement('textarea');
        el.value = value;
        el.setAttribute('readonly', '');
        el.style.position = 'fixed';
        el.style.top = '-9999px';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopySnackbarText('คัดลอกเบอร์โทรแล้ว');
      setCopySnackbarOpen(true);
    } catch (_) {
      setCopySnackbarText('คัดลอกไม่สำเร็จ');
      setCopySnackbarOpen(true);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, width: { xs: '100%', sm: 'auto' } }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ borderRadius: 1, width: { xs: '100%', sm: 'auto' }, minHeight: { xs: 44, sm: 36 }, borderColor: 'rgba(132, 94, 194, 0.5)', color: '#845EC2', '&:hover': { borderColor: '#845EC2', bgcolor: 'rgba(132, 94, 194, 0.08)' } }}>
          กลับ
        </Button>
      </Box>

      <Card
        elevation={0}
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          boxShadow: 'none',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(to right, #8E6CEB, #4092E9)',
            px: 2,
            pt: 1.5,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
          }}
        >
          
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'white', flex: 1 }}>
            รายละเอียด
          </Typography>
        </Box>
        <CardContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h5" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>
              เรื่อง : {ticket.title || '-'}
            </Typography>
          </Box>

          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ color: 'rgba(0, 0, 0, 0.55)' }}>
              สถานที่: {ticket.location || '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ color: 'rgba(0, 0, 0, 0.55)' }}>
              ที่อยู่: {ticket.address ?? '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ color: 'rgba(0, 0, 0, 0.55)' }}>
              ผู้แจ้ง: {ticket.owner_fullname ?? '-'}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div" sx={{ color: 'rgba(0, 0, 0, 0.55)', display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              โทร:{' '}
              <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Box
                  component="span"
                  onClick={() => ticket.telephone && copyToClipboard(ticket.telephone)}
                  sx={{
                    cursor: ticket.telephone ? 'pointer' : 'default',
                    textDecoration: ticket.telephone ? 'underline' : 'none',
                    textUnderlineOffset: '2px',
                    color: ticket.telephone ? '#6344A4' : 'inherit',
                    fontWeight: ticket.telephone ? 600 : 400,
                  }}
                  role={ticket.telephone ? 'button' : undefined}
                  tabIndex={ticket.telephone ? 0 : undefined}
                  onKeyDown={(e) => {
                    if (!ticket.telephone) return;
                    if (e.key === 'Enter' || e.key === ' ') copyToClipboard(ticket.telephone);
                  }}
                >
                  {ticket.telephone || '-'}
                </Box>
                {ticket.telephone ? (
                  <Tooltip title="คลิกเพื่อคัดลอก">
                    <IconButton
                      size="small"
                      onClick={() => copyToClipboard(ticket.telephone)}
                      sx={{ color: '#845EC2' }}
                      aria-label="คัดลอกเบอร์โทร"
                    >
                      <ContentCopyIcon fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ color: 'rgba(0, 0, 0, 0.55)', mb: 0.75 }}>
              ความเร่งด่วน
            </Typography>
            {(() => {
              const u = getUrgencyDisplay(ticket);
              return (
                <Chip
                  icon={<FlagIcon sx={{ fontSize: 18, color: 'inherit' }} />}
                  label={u.label}
                  size="small"
                  sx={{
                    borderRadius: '9999px',
                    bgcolor: u.bgcolor,
                    color: u.color,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              );
            })()}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', mb: 1 }}>
            รายละเอียด
          </Typography>
          <Typography variant="body1" sx={{ color: 'rgba(0, 0, 0, 0.7)', whiteSpace: 'pre-wrap', mb: 2 }}>
            {ticket.description || '-'}
          </Typography>

          {attachments.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', mb: 1 }}>
                ไฟล์แนบ
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                {attachments.map((att) => {
                  const url = att.file;
                  const fileName = url ? String(url).split('/').pop() || `ไฟล์ ${att.id}` : `ไฟล์ ${att.id}`;
                  const img = isImage(url);
                  if (img) {
                    return (
                      <Box
                        key={att.id}
                        onClick={() => setImagePreviewUrl(url)}
                        sx={{
                          width: 120,
                          height: 120,
                          borderRadius: 1,
                          border: '1px solid rgba(0,0,0,0.12)',
                          overflow: 'hidden',
                          cursor: 'pointer',
                          flexShrink: 0,
                          bgcolor: 'rgba(0,0,0,0.04)',
                          '&:hover': { borderColor: '#845EC2', boxShadow: '0 2px 8px rgba(132, 94, 194, 0.2)' },
                        }}
                      >
                        <img
                          src={url}
                          alt={fileName}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    );
                  }
                  return (
                    <Box
                      key={att.id}
                      component="a"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        border: '1px solid rgba(0,0,0,0.12)',
                        bgcolor: 'rgba(255,255,255,0.6)',
                        textDecoration: 'none',
                        color: 'inherit',
                        '&:hover': { bgcolor: 'rgba(132, 94, 194, 0.08)' },
                      }}
                    >
                      <AttachFileIcon sx={{ fontSize: 20, color: '#845EC2' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.7)' }}>
                        {fileName}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          <Dialog
            open={Boolean(imagePreviewUrl)}
            onClose={() => setImagePreviewUrl(null)}
            maxWidth={false}
            PaperProps={{
              sx: {
                maxWidth: '95vw',
                maxHeight: '95vh',
                bgcolor: 'transparent',
                boxShadow: 'none',
              },
            }}
            BackdropProps={{ sx: { bgcolor: 'rgba(0,0,0,0.85)' } }}
          >
            <IconButton
              onClick={() => setImagePreviewUrl(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            <DialogContent sx={{ p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {imagePreviewUrl && (
                <img
                  src={imagePreviewUrl}
                  alt=""
                  style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }}
                />
              )}
            </DialogContent>
          </Dialog>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', mb: 1 }}>
            สถานะ
          </Typography>
          {(() => {
            const s = getStatusDisplay(ticket.status);
            return (
              <Chip
                icon={s.icon}
                label={s.label}
                size="medium"
                sx={{
                  borderRadius: '9999px',
                  bgcolor: s.bgcolor,
                  color: s.color,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  py: 1.25,
                  px: 1.5,
                  border: 'none',
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            );
          })()}
          <List dense disablePadding sx={{ mt: 1.5 }}>
            <ListItem sx={{ py: 0.5, px: 0 }}>
              <ListItemText
                primary="สร้างเมื่อ"
                secondary={formatDate(ticket.created_at)}
                primaryTypographyProps={{ sx: { color: 'rgba(0, 0, 0, 0.6)' } }}
                secondaryTypographyProps={{ sx: { color: 'rgba(0, 0, 0, 0.5)' } }}
              />
            </ListItem>
          </List>

          {logs.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(0, 0, 0, 0.6)', mb: 1.5 }}>
                ประวัติการเปลี่ยนสถานะ
              </Typography>

              {/* Mobile: card stack */}
              <Stack spacing={1.5} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                {sortedStatusLogs.map((log, idx) => {
                  const statusKey = (log.status || '').trim() || null;
                  const statusDisplay = getStatusDisplay(statusKey);
                  const desc = log.description != null ? String(log.description).trim() : '';
                  const targetDepartment = log.department_name || (log.department != null ? `#${log.department}` : '');
                  return (
                    <Card
                      key={log.id ?? idx}
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: '1px solid rgba(0, 0, 0, 0.08)',
                        bgcolor: 'rgba(255, 255, 255, 0.9)',
                      }}
                    >
                      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
                          <Typography variant="caption" sx={{ fontWeight: 700, color: 'rgba(0, 0, 0, 0.45)' }}>
                            ลำดับ : {idx + 1}
                          </Typography>
                          <Chip
                            size="small"
                            label={statusDisplay.label}
                            icon={statusDisplay.icon}
                            sx={{
                              borderRadius: '9999px',
                              bgcolor: statusDisplay.bgcolor,
                              color: statusDisplay.color,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              height: 26,
                              '& .MuiChip-icon': { color: 'inherit', fontSize: 16 },
                            }}
                          />
                        </Box>
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" component="span" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: 600, flexShrink: 0, minWidth: 92 }}>
                              วันเวลา
                            </Typography>
                            <Typography variant="body2" component="span" sx={{ color: 'rgba(0, 0, 0, 0.75)', flex: 1, minWidth: 0 }}>
                              {formatDate(log.created_at)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" component="span" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: 600, flexShrink: 0, minWidth: 92 }}>
                              ผู้ดำเนินการ
                            </Typography>
                            <Typography variant="body2" component="span" sx={{ color: 'rgba(0, 0, 0, 0.7)', flex: 1, minWidth: 0 }}>
                              {log.operator_fullname || '—'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" component="span" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: 600, flexShrink: 0, minWidth: 92, pt: 0.125 }}>
                              หมายเหตุ
                            </Typography>
                            <Typography variant="body2" component="span" sx={{ color: 'rgba(0, 0, 0, 0.65)', flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {desc || '—'}
                            </Typography>
                          </Box>
                          {targetDepartment ? (
                            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="caption" component="span" sx={{ color: 'rgba(0, 0, 0, 0.5)', fontWeight: 600, flexShrink: 0, minWidth: 92 }}>
                                หน่วยงาน
                              </Typography>
                              <Typography variant="body2" component="span" sx={{ color: 'rgba(0, 0, 0, 0.7)', flex: 1, minWidth: 0 }}>
                                {targetDepartment}
                              </Typography>
                            </Box>
                          ) : null}
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>

              {/* Tablet & up: table */}
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  borderRadius: 2,
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  bgcolor: 'rgba(255, 255, 255, 0.85)',
                  overflowX: 'auto',
                }}
              >
                <Table size="small" sx={{ minWidth: 520 }}>
                  <TableHead>
                    <TableRow
                      sx={{
                        bgcolor: 'rgba(132, 94, 194, 0.08)',
                        '& th': { fontWeight: 700, color: 'rgba(0, 0, 0, 0.65)', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
                      }}
                    >
                      <TableCell width={56} align="center">
                        ลำดับ
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>วันเวลา</TableCell>
                      <TableCell>สถานะ</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>ผู้ดำเนินการ</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>หน่วยงาน</TableCell>
                      <TableCell>หมายเหตุ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedStatusLogs.map((log, idx) => {
                      const statusKey = (log.status || '').trim() || null;
                      const statusDisplay = getStatusDisplay(statusKey);
                      const desc = log.description != null ? String(log.description).trim() : '';
                      const targetDepartment = log.department_name || (log.department != null ? `#${log.department}` : '');
                      return (
                        <TableRow
                          key={log.id ?? idx}
                          hover
                          sx={{ '&:last-child td': { borderBottom: 0 }, '& td': { verticalAlign: 'top', py: 1.25 } }}
                        >
                          <TableCell align="center" sx={{ color: 'rgba(0, 0, 0, 0.45)', fontWeight: 600 }}>
                            {idx + 1}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(0, 0, 0, 0.7)', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>
                            {formatDate(log.created_at)}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={statusDisplay.label}
                              icon={statusDisplay.icon}
                              sx={{
                                borderRadius: '9999px',
                                bgcolor: statusDisplay.bgcolor,
                                color: statusDisplay.color,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                height: 26,
                                '& .MuiChip-icon': { color: 'inherit', fontSize: 16 },
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(0, 0, 0, 0.65)', fontSize: '0.8125rem' }}>
                            {log.operator_fullname || '—'}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(0, 0, 0, 0.65)', fontSize: '0.8125rem' }}>
                            {targetDepartment || '—'}
                          </TableCell>
                          <TableCell sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.8125rem', maxWidth: 280 }}>
                            {desc || '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setCopySnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setCopySnackbarOpen(false)}
          severity={copySnackbarText === 'คัดลอกเบอร์โทรแล้ว' ? 'success' : 'warning'}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {copySnackbarText}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TicketDetailPage;
